<script setup>
import { inject, ref } from 'vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import SettingsSection from '@/components/settings/SettingsSection.vue';
import BrandPreview from './BrandPreview.vue';
import { SCHOOL_SETTINGS_KEY } from '@/composables/useSchoolSettingsForm';
import { useBranding } from '@/branding';
import { required } from '@/utils/validation';

const KEYS = ['name', 'shortName', 'tagline', 'brandColor'];
const PRESETS = ['#E35E93', '#DC2626', '#EA580C', '#CA8A04', '#16A34A', '#0D9488', '#2563EB', '#7C3AED'];

const { form, saving, savedAt, isDirty, reset, save } = inject(SCHOOL_SETTINGS_KEY);
const branding = useBranding();
const formRef = ref(null);

const hexColor = (v) => /^#[0-9a-fA-F]{6}$/.test(v || '') || 'Use a hex colour like #E35E93.';

async function submit() {
  const { valid } = await formRef.value.validate();
  if (valid) save('identity', KEYS);
}
</script>

<template>
  <SettingsSection
    title="School identity"
    description="How the school appears in the sidebar, sign-in page, browser tab and emails."
    :dirty="isDirty(KEYS)"
    :saving="saving === 'identity'"
    :saved-at="savedAt.identity || 0"
    @save="submit"
    @reset="reset(KEYS)"
  >
    <v-form ref="formRef" class="form-stack" @submit.prevent="submit">
      <UiFormField label="School name" required v-slot="{ id }">
        <v-text-field :id="id" v-model="form.name" :rules="[required]" maxlength="150" hide-details="auto" />
      </UiFormField>

      <div class="form-grid">
        <UiFormField
          label="Short name"
          description="Used where space is tight, e.g. SDNHS."
          required
          v-slot="{ id }"
        >
          <v-text-field :id="id" v-model="form.shortName" :rules="[required]" maxlength="30" hide-details="auto" />
        </UiFormField>

        <UiFormField label="Brand colour" required v-slot="{ id }">
          <div class="color">
            <label class="color__swatch" :style="{ background: form.brandColor }">
              <input v-model="form.brandColor" type="color" aria-label="Pick brand colour" />
            </label>
            <v-text-field
              :id="id"
              v-model="form.brandColor"
              :rules="[hexColor]"
              maxlength="7"
              hide-details="auto"
            />
          </div>
        </UiFormField>
      </div>

      <div class="presets" role="group" aria-label="Suggested colours">
        <button
          v-for="c in PRESETS"
          :key="c"
          type="button"
          class="presets__item"
          :class="{ 'presets__item--active': form.brandColor.toUpperCase() === c }"
          :style="{ background: c }"
          :aria-label="`Use ${c}`"
          @click="form.brandColor = c"
        />
      </div>

      <UiFormField label="Tagline" description="Optional motto shown on the sign-in page." v-slot="{ id }">
        <v-text-field :id="id" v-model="form.tagline" maxlength="200" hide-details="auto" />
      </UiFormField>

      <BrandPreview
        :name="form.name"
        :short-name="form.shortName"
        :tagline="form.tagline"
        :color="form.brandColor"
        :logo-url="branding.logoUrl"
      />
    </v-form>
  </SettingsSection>
</template>

<style scoped>
.color {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.color__swatch {
  position: relative;
  flex: 0 0 36px;
  height: 36px;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}
.color__swatch input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.color :deep(.v-input) {
  flex: 1;
}
.presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: -8px;
}
.presets__item {
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}
.presets__item--active {
  box-shadow: 0 0 0 2px var(--background), 0 0 0 4px var(--foreground);
}
.presets__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--background), 0 0 0 4px var(--faint-foreground);
}
</style>
